/**
 * Shared YouTube video ID sanitizer.
 *
 * Extracted from VideoSection.jsx (T-209-17b) so both VideoSection and
 * vb-tipp's FifaYouTubeFeed can reuse the same XSS-defence implementation
 * without copy-paste. Behaviour is byte-for-byte identical to the previous
 * inline implementation — VideoSection.test.jsx continues to pass unchanged
 * (regression contract AC-209-SAN-01).
 *
 * Valid IDs are 11 characters from the YouTube alphabet:
 *   [A-Za-z0-9_-]{11}
 */

/**
 * Regex matching a single valid YouTube video ID.
 * Exported so callers that need bare validation can reuse the constant.
 */
export const YOUTUBE_ID_REGEX = /^[a-zA-Z0-9_-]{11}$/;

/**
 * Sanitize and validate a YouTube video ID to prevent XSS attacks.
 *
 * - `null` / `undefined` / empty string / non-string input → returns `null`
 *   silently (no warning — these are the "absent value" cases, not
 *   adversarial / malformed input worth surfacing).
 * - Otherwise trims leading/trailing whitespace before validating.
 * - Returns the trimmed ID on match.
 * - On a non-empty string that fails the regex (truly malformed /
 *   potentially adversarial input), logs `console.warn` and returns `null`.
 *
 * @param {unknown} id - The candidate YouTube video ID.
 * @returns {string|null} The sanitized ID, or `null` if invalid.
 */
export function sanitizeYouTubeId(id) {
  if (!id || typeof id !== 'string') {
    return null;
  }
  // Trim whitespace and validate format
  const trimmed = id.trim();
  if (YOUTUBE_ID_REGEX.test(trimmed)) {
    return trimmed;
  }
  console.warn('Invalid YouTube video ID rejected:', id);
  return null;
}
